import { useCallback, useContext, useMemo } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { OnPremiseFulfilmentData, ReviewedCart } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

import {
    clickEditMobileNumber,
    clickEditOrderType,
    clickSelectOrderType,
    clickOrderTypeHalfCardClose,
    clickOrderTypeHalfCardConfirmOrUpdate,
    clickOrderTypeHalfCardDeliver,
    clickOrderTypeHalfCardMobileNumber,
    clickOrderTypeHalfCardPickup,
    impressionOrderTypeHalfCard,
} from "@cart-client/analytics/events/OnPremiseFulfilment";
import { ORDER_PREFERENCE } from "@swiggy-private/cart-ui";
import { getCartId, getRestaurantId } from "@cart-client/helpers/Cart";

export const useOnPremiseFulfilmentAnalytics = (
    cartResponse: ReviewedCart | null,
    onPremiseData?: OnPremiseFulfilmentData | null,
) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const restaurantId = useMemo(() => getRestaurantId(cartResponse), [cartResponse]);
    const cartId = useMemo(() => getCartId(cartResponse), [cartResponse]);

    const basePayload = useMemo(() => ({ restaurantId, cartId }), [restaurantId, cartId]);

    const trackSelectOrderType = useCallback(() => {
        clickSelectOrderType(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackEditOrderType = useCallback(() => {
        clickEditOrderType(clickEvent, {
            ...basePayload,
            currentSeatNum: onPremiseData?.seat_number ?? "",
            currentScreen: onPremiseData?.audi_number ?? "",
        });
    }, [clickEvent, basePayload, onPremiseData?.audi_number, onPremiseData?.seat_number]);

    const trackEditMobileNumber = useCallback(() => {
        clickEditMobileNumber(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackOrderTypeCtaClick = useCallback(() => {
        if (!isEmpty(onPremiseData?.order_preference)) {
            trackEditOrderType();
        } else {
            trackSelectOrderType();
        }
    }, [onPremiseData?.order_preference, trackEditOrderType, trackSelectOrderType]);

    const trackOrderTypeHalfCardImpression = useCallback(() => {
        impressionOrderTypeHalfCard(impressionEvent, basePayload);
    }, [impressionEvent, basePayload]);

    const trackOrderTypeHalfCardDeliver = useCallback(() => {
        clickOrderTypeHalfCardDeliver(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackOrderTypeHalfCardPickup = useCallback(() => {
        clickOrderTypeHalfCardPickup(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackOrderTypeHalfCardMobileNumber = useCallback(() => {
        clickOrderTypeHalfCardMobileNumber(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackOrderTypeHalfCardClose = useCallback(() => {
        clickOrderTypeHalfCardClose(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackOrderTypeHalfCardConfirm = useCallback(
        (orderType: string, seatNum: string, screen: string, isUpdate: boolean) => {
            const isPickup = orderType === ORDER_PREFERENCE.PICKUP_AT_COUNTER;
            clickOrderTypeHalfCardConfirmOrUpdate(
                clickEvent,
                {
                    ...basePayload,
                    orderType,
                    ...(isPickup ? {} : { seatNum, screen }),
                },
                isUpdate,
            );
        },
        [clickEvent, basePayload],
    );

    return {
        trackSelectOrderType,
        trackEditOrderType,
        trackEditMobileNumber,
        trackOrderTypeCtaClick,
        trackOrderTypeHalfCardImpression,
        trackOrderTypeHalfCardDeliver,
        trackOrderTypeHalfCardPickup,
        trackOrderTypeHalfCardMobileNumber,
        trackOrderTypeHalfCardClose,
        trackOrderTypeHalfCardConfirm,
    };
};
